import { ExternalLink, PlugZap, RefreshCw } from "lucide-react";

import { Button } from "components/ui/Button";
import { PcrsPanel } from "features/pcrsExtension/components/PcrsPanel/PcrsPanel";
import {
  PCRS_EXTENSION_ID,
  PCRS_EXTENSION_NAME,
  PCRS_EXTENSION_STORE_URL,
} from "features/pcrsExtension/constants/pcrsExtension";
import { BROWSERS, detectBrowser } from "features/pcrsExtension/utils/detectBrowser";
import { isExtensionMessagingAvailable } from "features/pcrsExtension/utils/pcrsExtensionBridge";

import styles from "./PcrsExtensionRequired.module.css";

const InstallSteps = ({ browser }) => {
  if (browser === BROWSERS.EDGE) {
    return (
      <>
        click <em>Get the extension</em>, accept <em>Allow extensions from other stores</em>, then click{" "}
        <em>Add to Chrome</em> and <em>Add extension</em>.
      </>
    );
  }

  return (
    <>
      click <em>Get the extension</em>, then <em>Add to Chrome</em> and <em>Add extension</em>.
    </>
  );
};

// Shown in place of a PCRS check when the extension isn't installed, so a new
// user is told how to get it rather than seeing a failed check.
export const PcrsExtensionRequired = ({ onRecheck, isRechecking = false }) => {
  const browser = detectBrowser();
  const canInstall = browser !== BROWSERS.OTHER;

  // A tab opened before the install can't see the extension until it reloads.
  const handleRecheck = () => {
    if (!isExtensionMessagingAvailable()) {
      window.location.reload();

      return;
    }

    onRecheck();
  };

  return (
    <PcrsPanel
      icon={PlugZap}
      title="PCRS Scheme Checker not available."
      lead={
        <>
          PCRS Scheme Checker requires installing browser Extension: {PCRS_EXTENSION_NAME} (plugin id:{" "}
          <code className={styles.code}>{PCRS_EXTENSION_ID}</code>).
        </>
      }
      data-testid="pcrs-extension-required"
    >
      {!canInstall && (
        <p className={styles.installText} data-testid="pcrs-extension-unsupported">
          This browser is not supported at present. For further questions, contact Kinesin support.
        </p>
      )}

      {canInstall && (
        <div className={styles.install} data-testid="pcrs-extension-install">
          <p className={styles.installText}>
            To install: <InstallSteps browser={browser} />
          </p>
          <div className={styles.actions}>
            <Button
              as="a"
              href={PCRS_EXTENSION_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              endIcon={ExternalLink}
              data-testid="pcrs-extension-store-link"
            >
              Get the extension
            </Button>
            <Button
              variant="outline"
              size="small"
              startIcon={RefreshCw}
              onClick={handleRecheck}
              disabled={isRechecking}
              data-testid="pcrs-extension-recheck"
            >
              {isRechecking ? "Checking…" : "Check again"}
            </Button>
          </div>
        </div>
      )}
    </PcrsPanel>
  );
};
